import { useEffect, useState } from "react";

import {
  fetchCumiFareyeLocations,
  type CumiFareyeLocation,
} from "@/lib/supabase-api";
import { Input } from "@/components/Input";
import { LoadingSpinner } from "@/components/LoadingSpinner";

interface CumiSuburbLookupProps {
  value: string;
  selectedCode: string;
  error?: string;
  onSuburbChange: (value: string) => void;
  onLocationSelect: (location: CumiFareyeLocation | null) => void;
}

export function CumiSuburbLookup({
  value,
  selectedCode,
  error,
  onSuburbChange,
  onLocationSelect,
}: CumiSuburbLookupProps) {
  const [locations, setLocations] = useState<CumiFareyeLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const suburb = value.trim();
    if (!suburb || selectedCode) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      setLookupError(null);

      try {
        const results = await fetchCumiFareyeLocations(
          suburb,
          abortController.signal,
        );
        setLocations(results);
        setIsOpen(results.length > 0);
        if (results.length === 0) {
          setLookupError("No delivery codes found for this suburb");
        }
      } catch {
        if (abortController.signal.aborted) return;
        setLocations([]);
        setLookupError("Unable to load delivery codes. Please try again.");
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [selectedCode, value]);

  const handleSuburbChange = (nextValue: string) => {
    setLocations([]);
    setLookupError(null);
    setIsOpen(false);
    onLocationSelect(null);
    onSuburbChange(nextValue);
  };

  const handleCodeChange = (code: string) => {
    const location =
      locations.find((candidate) => candidate.code === code) ?? null;
    onLocationSelect(location);
    setIsOpen(false);
  };

  const codeOptions =
    selectedCode && !locations.some((location) => location.code === selectedCode)
      ? [{ code: selectedCode }, ...locations]
      : locations;

  return (
    <div className="relative">
      <label
        htmlFor="cumi-suburb-search"
        className="block text-[13px] font-medium mb-1.5 text-(--color-text-secondary)"
      >
        Suburb <span className="text-(--color-error)">*</span>
      </label>
      <Input
        id="cumi-suburb-search"
        type="search"
        role="combobox"
        aria-label="Search suburb and select a delivery code"
        aria-autocomplete="list"
        aria-controls="cumi-delivery-code-options"
        aria-expanded={isOpen && codeOptions.length > 0}
        value={value}
        onChange={(event) => handleSuburbChange(event.target.value)}
        onFocus={() => {
          if (codeOptions.length > 0) setIsOpen(true);
        }}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setIsOpen(false);
        }}
        error={error}
        placeholder="Search suburb"
        required
      />

      {isLoading && (
        <div className="mt-2 flex items-center gap-2 text-[13px] text-(--color-text-muted)">
          <LoadingSpinner size="sm" />
          <span>Searching delivery codes...</span>
        </div>
      )}

      {!isLoading && isOpen && codeOptions.length > 0 && (
        <div
          id="cumi-delivery-code-options"
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-(--color-surface-border) bg-(--color-surface-primary) py-1 shadow-lg"
        >
          {codeOptions.map((location) => (
            <button
              key={location.code}
              type="button"
              role="option"
              aria-selected={location.code === selectedCode}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleCodeChange(location.code)}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[14px] text-(--color-text-primary) hover:bg-(--color-bg-hover) focus:bg-(--color-bg-hover) focus:outline-none"
            >
              <span>
                {"suburb" in location ? location.suburb || value : value}
              </span>
              <span className="text-[12px] text-(--color-text-muted)">
                {location.code}
              </span>
            </button>
          ))}
        </div>
      )}

      {!isLoading && lookupError && (
        <p className="mt-2 text-[13px] text-(--color-error)">{lookupError}</p>
      )}
    </div>
  );
}
